import { Asset, Icon, NeutralBadge, SuccessBadge, TAsset } from '@hz-design-system/web-ui';
import React from 'react';
import { useI18nContext } from '@ecg-marktplaats/js-react-i18n';
import { v4 as uuidv4 } from 'uuid';
import { TAugmentedLabel, TCarrierId, TSelfShippingLabel, TLabel } from '@ecg-internal/bff-buyers-domain';

import { TListingClient } from 'types/TListing';
import ShippingModalLink from './Shipping/ModalLink/ModalLink';
import Classes from './ShippingInformation.module.scss';

import { DELIVERY_TYPE } from '../../../../utils/constants';

type TShippingInformationProps = {
  shippingInformation: TListingClient['shippingInformation'];
  shippable: boolean;
  isAdmarkt: boolean;
};

type TExtendedLabelsProps = {
  carrierId: TCarrierId | null | undefined;
  labels: TLabel[];
  promoText?: string;
  isLink: boolean;
};

const ShippingInformation = ({ shippingInformation, shippable, isAdmarkt }: TShippingInformationProps) => {
  const { t } = useI18nContext();
  const { deliveryType, augmentedLabels, mappedShippingOptions } = shippingInformation;
  //@ts-expect-error -- type conversion
  const isBrengerIncluded: TExtendedLabelsProps[] = augmentedLabels?.find((item) => item.carrierId === 'brenger');
  const getCarrierIcon = (carrierId: string): TAsset => {
    switch (carrierId) {
      case 'bpost':
        return `icon-logo-bpost-variant`;
      case 'budbee':
        return 'icon-logo-budbee-round';
      case 'dhl-nl':
        return 'icon-logo-dhl';
      case 'brenger':
      case 'postnl':
        return `icon-logo-${carrierId}`;
      default:
        return '' as TAsset;
    }
  };

  const listingHasShipping =
    deliveryType?.attributeValueKey === DELIVERY_TYPE.SHIP ||
    deliveryType?.attributeValueKey === DELIVERY_TYPE.PICK_UP_OR_SHIP;

  //@ts-expect-error type conversion
  const brengerAugmentedLabel: TExtendedLabelsProps[] = augmentedLabels?.filter((item) => item.carrierId === 'brenger');

  const filteredAugmentedLabels = !listingHasShipping && isBrengerIncluded && brengerAugmentedLabel?.length > 0;
  // do not render an empty block if nothing to show
  if (!deliveryType && !shippable) {
    return null;
  }
  const containerClassname = (carrierId) => {
    if (carrierId && carrierId !== 'brenger') {
      return '';
    } else if (carrierId && carrierId === 'brenger') {
      return Classes.brenger;
    } else {
      return Classes.baseLine;
    }
  };

  const NonClickableShippingComponent = ({ cost }) => (
    <div className={`${Classes.logoAndInfo} ${Classes.flexStart}`}>
      <Icon name="truck" />
      <span>{t('shipping.costInfo', { cost })}</span>
    </div>
  );

  const ExtendedLabelsComponent = ({ carrierId, labels, promoText, isLink }: TExtendedLabelsProps) => (
    <div className={`${Classes.logoAndInfo} ${containerClassname(carrierId)}`}>
      <div className={Classes.logo}>
        {carrierId ? <Asset.Square name={getCarrierIcon(carrierId)} size="m" /> : <Icon name="truck" />}
      </div>
      <div className={Classes.info}>
        {labels.reduce((result, { label, price }: TLabel) => {
          const key = uuidv4();
          const labelString = `${label} `;
          const priceString = promoText || !isLink ? `<strong>${price}</strong> ` : `${price} `;
          const resultString = labelString + priceString;
          return [
            ...result,
            <span key={key}>
              {/* oxlint-disable-next-line react/no-danger */}
              <span dangerouslySetInnerHTML={{ __html: resultString }} />
              {promoText && <SuccessBadge>{promoText}</SuccessBadge>}
            </span>,
          ];
        }, [] as React.ReactNode[])}
      </div>
    </div>
  );

  const showCorrectShippingComponent = () => {
    if (listingHasShipping && !isAdmarkt && augmentedLabels?.length !== 0) {
      return augmentedLabels?.map(
        ({ shouldShowMoreInfo, carrierId, labels, promoText }: TAugmentedLabel | TSelfShippingLabel) => {
          const key = uuidv4();
          if (shouldShowMoreInfo) {
            return (
              <ShippingModalLink key={key} shippingOptions={mappedShippingOptions}>
                <ExtendedLabelsComponent carrierId={carrierId} labels={labels} promoText={promoText} isLink={true} />
              </ShippingModalLink>
            );
          }

          return (
            <ExtendedLabelsComponent
              carrierId={carrierId}
              labels={labels}
              key={key}
              isLink={false}
              promoText={promoText}
            />
          );
        },
      );
    } else if (filteredAugmentedLabels) {
      const key = uuidv4();
      return (
        <ExtendedLabelsComponent
          carrierId={brengerAugmentedLabel[0].carrierId}
          labels={brengerAugmentedLabel[0].labels}
          key={key}
          isLink={false}
        />
      );
    } else {
      const { labels } = augmentedLabels?.[0] || {};
      const price = labels?.[0]?.price;
      return price && listingHasShipping ? <NonClickableShippingComponent cost={price} /> : null;
    }
  };

  return (
    <div className={Classes.root}>
      {deliveryType && (
        <NeutralBadge className={Classes.deliveryLabel}>{deliveryType.attributeValueLabel}</NeutralBadge>
      )}
      <div className={Classes.shipping}>{showCorrectShippingComponent()}</div>
    </div>
  );
};

export default ShippingInformation;
